/**
 * Portfolio Service
 *
 * API service for the Agency Brand Portfolio page.
 * Fetches all client workspaces with health, pipeline, and operational signals.
 *
 * Maps the backend PortfolioClientResponse shape to the frontend PortfolioClient
 * interface used across all portfolio views.
 */

import { normalizeRedactableFields } from '@/utils/portfolioRedaction';
import { extractPortfolioLockFields } from '@/utils/portfolioLocks';
import apiClient from './api';
import type {
  PortfolioClient,
} from '@/data/portfolio.types';
import type { ColorPill, VibeType } from '@/types/portfolio';

// ── Response shape from the backend ──────────────────────────────────────────

export interface GetPortfolioResponse {
  clients: PortfolioClient[];
  /**
   * Feature keys the AGENCY package lacks (portfolio_* / release_* / brand_portfolio). Non-empty only when the
   * backend enforces package gating; an empty array = nothing is locked.
   */
  lockedFeatures: string[];
  /** Cheapest plan slug per locked key (optional backend field), for the locked cards */
  lockedFeatureUpsell: Record<string, string>;
}

// ── Raw backend response type (mirrors backend/src/types/portfolio.types.ts) ──

interface RawPortfolioClientResponse {
  id: string;
  name: string;
  avatarInitial: string;
  avatarColor: string;
  logoUrl?: string | null;
  icon?: string | null;
  customColor?: string | null;
  carteBlanche?: boolean;
  isActive?: boolean;
  hiddenByPlan?: boolean;
  pausedAt?: string | null;
  reactivationAvailableAt?: string | null;
  ownerId: number | null;
  ownerName: string | null;
  ownerInitials: string | null;
  ownerAvatarColor: string | null;
  ownerProfilePhotoUrl: string | null;
  clientSize: 'enterprise' | 'smb';
  lifecycleStage: 'active' | 'onboarding' | 'churning' | 'paused';
  satisfactionSignal: 'STRONG' | 'NEUTRAL' | 'TENSE' | null;
  // Fields below may be null when the agency package lacks the feature (enforce); see utils/portfolioRedaction
  healthOverall: 'on_track' | 'watch' | 'at_risk' | 'off_track' | 'paused' | null;
  healthOperational: { score: number; signals: { label: string; score: number }[] } | null;
  healthBrandEquity: { score: number; signals: { label: string; score: number }[] } | null;
  healthCommercial: { score: number; signals: { label: string; score: number }[] } | null;
  weeklyScoreHistory: number[] | null;
  pipeline: {
    pending: number;
    unscheduled: number;
    awaitingApproval: number;
    changesRequested: number;
    approved: number;
    scheduled: number;
    publishedThisWeek: number;
    failed: number;
    partial: number;
    missed: number;
    carteBlanche: boolean;
  };
  pendingCount: number;
  stalePendingCount: number;
  calendarMappedCount: number;
  runwayDays: number | null;
  libraryApprovedCount: number;
  coverDays: number | null;
  runsDryOn: string | null;
  lowPipeline: boolean | null;
  coverBreakdown: { plannedDays: number; readyDays: number } | null;
  cadenceWeekly: number;
  velocity: { postsThisWeek: number; postsPriorWeek: number; trendPct: number } | null;
  channelHealth: { total: number; expiring: number; broken: number } | null;
  renewal: { daysUntilRenewal: number | null; atRisk: boolean } | null;
  latestStatusUpdate: {
    vibe: string;
    tldr: string;
    authorName: string;
    createdAt: string;
    colorPill: string | null;
    wins: string | null;
    blockers: string | null;
    nextFocus: string | null;
    strategicNote: string | null;
    postsPublished: number;
    postsPlanned: number;
    topPost: {
      id: number;
      caption: string;
      thumbnailUrl: string | null;
    } | null;
  } | null;
  scheduledNextDays: string[];
  postsThisWeek: number;
  recentActivity: {
    id: string;
    actorName: string;
    actionType: 'APPROVED' | 'CHANGES_REQUESTED' | 'REJECTED' | 'PENDING';
    postTitle?: string;
    comment?: string;
    occurredAt: string;
  }[];
  avgTimeToApproveHours: number | null;
  totalPosts: number;
  totalChannels: number;
  totalMembers: number;
  totalCalendars: number;
  assetInventory: {
    draftsPolishing: number;
    readyToPublish: number;
    scheduled: number;
    published: number;
    remixable: number;
  };
}

interface RawGetPortfolioResponse {
  clients: RawPortfolioClientResponse[];
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

// ── Mapping helper ────────────────────────────────────────────────────────────

function mapClient(raw: RawPortfolioClientResponse): { client: PortfolioClient; redactedFeatures: string[] } {
  const safe = normalizeRedactableFields(raw, raw.lifecycleStage === 'paused' || raw.isActive === false);
  const client: PortfolioClient = {
    // Backend returns id as string (team id); frontend uses number throughout
    id: parseInt(raw.id, 10),
    name: raw.name,
    logoUrl: raw.logoUrl ?? undefined,
    icon: raw.icon ?? null,
    customColor: raw.customColor ?? null,
    carteBlanche: raw.carteBlanche ?? false,
    isActive: raw.isActive,
    hiddenByPlan: raw.hiddenByPlan ?? false,
    pausedAt: raw.pausedAt ?? null,
    reactivationAvailableAt: raw.reactivationAvailableAt ?? null,
    avatarInitial: raw.avatarInitial,
    avatarColor: raw.avatarColor || 'bg-gray-300',
    clientSize: raw.clientSize,
    lifecycleStage: raw.lifecycleStage,
    satisfactionSignal: raw.satisfactionSignal,
    // Nullable owner fields — fall back to empty strings so components render safely
    ownerId: raw.ownerId ?? 0,
    ownerName: raw.ownerName ?? '',
    ownerInitials: raw.ownerInitials ?? '',
    ownerAvatarColor: raw.ownerAvatarColor ?? 'bg-gray-300',
    ownerProfilePhotoUrl: raw.ownerProfilePhotoUrl ?? null,
    healthOverall: safe.healthOverall,
    healthOperational: safe.healthOperational,
    healthBrandEquity: safe.healthBrandEquity,
    healthCommercial: safe.healthCommercial,
    weeklyScoreHistory: safe.weeklyScoreHistory,
    pipeline: {
      pending: raw.pipeline.pending,
      unscheduled: raw.pipeline.unscheduled ?? 0,
      awaitingApproval: raw.pipeline.awaitingApproval ?? 0,
      changesRequested: raw.pipeline.changesRequested,
      approved: raw.pipeline.approved,
      scheduled: raw.pipeline.scheduled,
      publishedThisWeek: raw.pipeline.publishedThisWeek,
      failed: raw.pipeline.failed,
      partial: raw.pipeline.partial ?? 0,
      missed: raw.pipeline.missed ?? 0,
      carteBlanche: raw.pipeline.carteBlanche ?? false,
    },
    pendingCount: raw.pendingCount,
    stalePendingCount: raw.stalePendingCount,
    calendarMappedCount: raw.calendarMappedCount ?? 0,
    runwayDays: safe.runwayDays,
    libraryApprovedCount: raw.libraryApprovedCount,
    coverDays: safe.coverDays,
    runsDryOn: safe.runsDryOn,
    lowPipeline: safe.lowPipeline,
    coverBreakdown: safe.coverBreakdown,
    cadenceWeekly: raw.cadenceWeekly,
    velocity: safe.velocity,
    channelHealth: safe.channelHealth,
    renewal: safe.renewal,
    latestStatusUpdate: raw.latestStatusUpdate
      ? {
          // Backend vibe is stored as a string emoji value
          vibe: raw.latestStatusUpdate.vibe as '😀' | '😐' | '😬' | '🔥',
          tldr: raw.latestStatusUpdate.tldr,
          authorName: raw.latestStatusUpdate.authorName,
          createdAt: raw.latestStatusUpdate.createdAt,
          colorPill: raw.latestStatusUpdate.colorPill,
          wins: raw.latestStatusUpdate.wins,
          blockers: raw.latestStatusUpdate.blockers,
          nextFocus: raw.latestStatusUpdate.nextFocus,
          strategicNote: raw.latestStatusUpdate.strategicNote,
          postsPublished: raw.latestStatusUpdate.postsPublished,
          postsPlanned: raw.latestStatusUpdate.postsPlanned,
          topPost: raw.latestStatusUpdate.topPost,
        }
      : undefined,
    scheduledNextDays: raw.scheduledNextDays,
    postsThisWeek: raw.postsThisWeek,
    recentActivity: raw.recentActivity,
    avgTimeToApproveHours: raw.avgTimeToApproveHours ?? undefined,
    totalPosts: raw.totalPosts,
    totalChannels: raw.totalChannels,
    totalMembers: raw.totalMembers,
    totalCalendars: raw.totalCalendars,
    assetInventory: raw.assetInventory,
  };
  return { client, redactedFeatures: safe.redactedFeatures };
}

// ── Portfolio Timeline (Brand Portfolio Timeline heat-map) ───────────────────

/** Per-day post counts for a single team, keyed by `YYYY-MM-DD` (computed
 *  server-side in the caller's timezone). Sparse — only non-zero days are
 *  returned by the backend. */
export interface TimelineDayBucket {
  date: string;
  /** Posts committed to a calendar day but not yet scheduled/published/failed
   *  ("planned"). Never overlaps published/scheduled/failed. */
  mapped: number;
  published: number;
  scheduled: number;
  /** First-class occurrence outcome — some channels published, some failed. Never folded into `failed`. */
  partial: number;
  failed: number;
  /** First-class occurrence outcome — overdue, never attempted. Never folded into `failed`. */
  missed: number;
}

/** Campaign marker (backend model `CalendarNote`, shown in the app as "Campaigns"). */
export interface TimelineNote {
  id: number;
  title: string;
  description: string | null;
  color: string | null;
  startAt: string;
  endAt: string | null;
  repeatType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | null;
  calendarName: string | null;
}

/** Public-holiday marker for a team's country/locale. */
export interface TimelineHoliday {
  id: number;
  holidayDate: string;
  localName: string;
  englishName: string | null;
  colorCode: string | null;
  countryName: string | null;
  calendarName: string | null;
}

export interface TimelineTeamBuckets {
  teamId: number;
  days: TimelineDayBucket[];
  notes: TimelineNote[];
  holidays: TimelineHoliday[];
}

export interface PortfolioTimelineResponse {
  from: string;
  to: string;
  teams: TimelineTeamBuckets[];
}

// ── Save status update payload ────────────────────────────────────────────────

export interface SaveStatusUpdatePayload {
  teamId: number;
  weekStart: string; // ISO datetime string (period start)
  periodEnd?: string; // ISO datetime string (period end, inclusive)
  colorPill: ColorPill;
  tldr: string;
  vibe?: VibeType | null;
  wins?: string;
  blockers?: string;
  nextFocus?: string;
  strategicNote?: string;
  topPostId?: number | null;
  postsPublished?: number;
  postsPlanned?: number;
}

export interface SaveStatusUpdateResponse {
  id: number;
  teamId: number;
  authorId: number;
  weekStart: string;
  periodEnd: string;
  colorPill: string;
  tldr: string;
  vibe: string | null;
  wins: string | null;
  blockers: string | null;
  nextFocus: string | null;
  strategicNote: string | null;
  topPostId: number | null;
  postsPublished: number;
  postsPlanned: number;
  createdAt: string;
  updatedAt: string;
  authorName: string;
}

// ── Status update history ────────────────────────────────────────────────────

export interface StatusUpdateHistoryItem {
  id: number;
  weekStart: string;
  periodEnd: string;
  colorPill: string;
  tldr: string;
  vibe: string | null;
  wins: string | null;
  blockers: string | null;
  nextFocus: string | null;
  strategicNote: string | null;
  postsPublished: number;
  postsPlanned: number;
  authorName: string;
  createdAt: string;
  topPost: {
    id: number;
    caption: string;
    thumbnailUrl: string | null;
  } | null;
}

// ── Status update metrics (period-scoped auto-fill) ──────────────────────────

/** Top-post candidate for a given period, sourced from live agency highlights. */
export interface TopPostCandidateDto {
  id: string;
  caption: string;
  metric: string;
  engagementRate?: number;
  thumbnailPlaceholderGradient: string;
}

export interface StatusUpdateMetrics {
  postsPublished: number;
  topPosts: TopPostCandidateDto[];
}

// ── Service ───────────────────────────────────────────────────────────────────

export const portfolioService = {
  /** GET /api/agency/portfolio */
  async getPortfolio(): Promise<GetPortfolioResponse> {
    // The lock fields are TOP-LEVEL on the body (next to `data`)
    const response = await apiClient.get<ApiEnvelope<RawGetPortfolioResponse> & Record<string, unknown>>(
      '/api/agency/portfolio',
    );
    const payload = response.data.data;
    const { lockedFeatures, lockedFeatureUpsell } = extractPortfolioLockFields(response.data);
    const mapped = payload.clients.map(mapClient);
    // Only when the server is enforcing (non-empty lockedFeatures) does a null field lock its feature; with an
    // empty list a single-brand null glitch must not lock a feature portfolio-wide (the nulls are just normalised)
    const redacted = lockedFeatures.length > 0 ? mapped.flatMap((m) => m.redactedFeatures) : [];
    return {
      clients: mapped.map((m) => m.client),
      lockedFeatures: Array.from(new Set([...lockedFeatures, ...redacted])),
      lockedFeatureUpsell,
    };
  },

  /** POST /api/agency/portfolio/status-update */
  async saveStatusUpdate(payload: SaveStatusUpdatePayload): Promise<SaveStatusUpdateResponse> {
    const response = await apiClient.post<ApiEnvelope<SaveStatusUpdateResponse>>(
      '/api/agency/portfolio/status-update',
      payload,
    );
    return response.data.data;
  },

  /** GET /api/agency/portfolio/status-updates/:teamId — recent updates, newest first */
  async getStatusUpdateHistory(
    teamId: number,
    limit = 8,
  ): Promise<StatusUpdateHistoryItem[]> {
    const response = await apiClient.get<ApiEnvelope<StatusUpdateHistoryItem[]>>(
      `/api/agency/portfolio/status-updates/${teamId}?limit=${limit}`,
    );
    return response.data.data;
  },

  /**
   * GET /api/agency/portfolio/status-update/metrics?teamId&start&end
   * Recomputes the "posts published" auto-fill and top-post picker candidates
   * for an arbitrary period (used by the compose modal's period picker).
   */
  async getStatusUpdateMetrics(
    teamId: number,
    startIso: string,
    endIso: string,
  ): Promise<StatusUpdateMetrics> {
    const params = new URLSearchParams({
      teamId: String(teamId),
      start: startIso,
      end: endIso,
    });
    const response = await apiClient.get<ApiEnvelope<StatusUpdateMetrics>>(
      `/api/agency/portfolio/status-update/metrics?${params.toString()}`,
    );
    return response.data.data;
  },

  /**
   * GET /api/agency/portfolio/timeline?from&to&timezone — per-brand, per-day
   * post buckets (published/scheduled/failed) for the Brand Portfolio
   * Timeline heat-map. `from`/`to` are ISO datetime strings; `timezone` is an
   * IANA zone name (the day buckets are keyed in that zone).
   */
  async getPortfolioTimeline(
    from: string,
    to: string,
    timezone: string,
  ): Promise<PortfolioTimelineResponse> {
    const params = new URLSearchParams({ from, to, timezone });
    const response = await apiClient.get<ApiEnvelope<PortfolioTimelineResponse>>(
      `/api/agency/portfolio/timeline?${params.toString()}`,
    );
    return response.data.data;
  },

  /** GET /api/agency/portfolio/highlights?period=… — Award + Repurpose strips */
  async getHighlights(
    period: 'today' | 'week' | 'month' | 'quarter' = 'week',
  ): Promise<GetHighlightsResponse> {
    const response = await apiClient.get<ApiEnvelope<GetHighlightsResponse>>(
      `/api/agency/portfolio/highlights?period=${period}`,
    );
    return response.data.data;
  },

  /** POST /api/agency/portfolio/plan-batch — file a batch brief against a team */
  async planBatch(payload: PlanBatchPayload): Promise<{ notificationId: number }> {
    const response = await apiClient.post<ApiEnvelope<{ notificationId: number }>>(
      '/api/agency/portfolio/plan-batch',
      payload,
    );
    return response.data.data;
  },

  /** GET /api/agency/portfolio/digest-preview — exactly what send-digest would email */
  async getDigestPreview(audience: 'team' | 'brand', teamId?: number): Promise<DigestPreviewResponse> {
    const response = await apiClient.get<ApiEnvelope<DigestPreviewResponse>>(
      '/api/agency/portfolio/digest-preview',
      { params: { audience, ...(teamId !== undefined ? { teamId } : {}) } },
    );
    return response.data.data;
  },

  /** POST /api/agency/portfolio/send-digest — queue a digest to recipients */
  async sendDigest(
    payload: SendDigestPayload,
  ): Promise<SendDigestResult> {
    const response = await apiClient.post<
      ApiEnvelope<SendDigestResult>
    >(
      '/api/agency/portfolio/send-digest',
      payload,
    );
    return response.data.data;
  },

  // ── Saved views CRUD ──────────────────────────────────────────────────────

  async listViews(): Promise<PortfolioViewRecord[]> {
    const response = await apiClient.get<ApiEnvelope<{ views: PortfolioViewRecord[] }>>(
      '/api/agency/portfolio/views',
    );
    return response.data.data.views;
  },

  async createView(payload: CreatePortfolioViewPayload): Promise<PortfolioViewRecord> {
    const response = await apiClient.post<ApiEnvelope<PortfolioViewRecord>>(
      '/api/agency/portfolio/views',
      payload,
    );
    return response.data.data;
  },

  async updateView(
    viewId: number,
    payload: UpdatePortfolioViewPayload,
  ): Promise<PortfolioViewRecord> {
    const response = await apiClient.patch<ApiEnvelope<PortfolioViewRecord>>(
      `/api/agency/portfolio/views/${viewId}`,
      payload,
    );
    return response.data.data;
  },

  async deleteView(viewId: number): Promise<void> {
    await apiClient.delete(`/api/agency/portfolio/views/${viewId}`);
  },
};

// ── Plan batch / Send digest payloads ─────────────────────────────────────────

export interface PlanBatchPayload {
  teamId: number;
  postCount: number;
  notes?: string;
}

export type DigestHealthTier = 'on_track' | 'watch' | 'at_risk' | 'off_track' | 'paused';

export interface DigestBrandRow {
  brandId: number;
  name: string;
  ownerName: string | null;
  tier: DigestHealthTier;
  detail?: string;
  metric?: string;
}

export interface DigestKpi {
  key?: 'brands' | 'on_track' | 'watch' | 'at_risk';
  label: string;
  value: string;
  accent: string;
}

/** Mirrors the backend digest payload: exactly what the email contains. */
export interface TeamDigestPreview {
  type: 'team';
  headline: string;
  subline: string;
  kpis: DigestKpi[];
  atRisk: DigestBrandRow[];
  cliffs: DigestBrandRow[];
  topPerformers: DigestBrandRow[];
  portfolioUrl: string;
}

export interface BrandDigestPreview {
  type: 'brand';
  brandId: number;
  brandName: string;
  ownerName: string | null;
  weekLabel: string;
  tier: DigestHealthTier;
  tldr?: string;
  vibeEmoji?: string;
  wins?: string;
  blockers?: string;
  nextFocus?: string;
  postsPublished: number;
  postsPlanned: number;
  cadenceDescription: string;
  coverDays: number;
  trendPct: number;
  brandUrl: string;
}

export interface DigestPreviewResponse {
  subject: string;
  digest: TeamDigestPreview | BrandDigestPreview;
}

export interface SendDigestResult {
  queuedCount: number;
  sentCount: number;
  failedCount: number;
  /** Recipients that were suppressed or blocked (dev guard): nothing was sent to them */
  skippedCount: number;
}

/**
 * Server is the single source of truth for digest content. The client picks
 * audience + (for brand mode) teamId; the backend queries fresh portfolio data
 * and renders the branded email itself.
 */
export interface SendDigestPayload {
  recipients: string[];
  audience: 'team' | 'brand';
  /** Required when audience === 'brand' */
  teamId?: number;
  /** Optional override; otherwise the server generates the subject */
  subject?: string;
}

// ── Highlights response ───────────────────────────────────────────────────────

/**
 * Single media item attached to a highlight post (matches frontend PostMedia shape).
 * Comes from the winning version (base post or selected variation) in display order.
 */
export interface HighlightMediaItem {
  id: number;
  type: 'IMAGE' | 'VIDEO' | 'PDF';
  originalUrl: string;
  thumbnailUrl: string | null;
  order: number;
  width: number | null;
  height: number | null;
  duration: number | null;
}

export interface PortfolioHighlightPost {
  id: string;
  clientId: string;
  clientName: string;
  clientAvatarColor: string;
  caption: string;
  /** Full media gallery (image/video/PDF) for the winning version of this post. */
  media: HighlightMediaItem[];
  thumbnailPlaceholderGradient: string;
  /** All platforms the post was cross-published to (deduped). Always at least one. */
  platforms: string[];
  /** Channel display names aligned to `platforms` by index. May contain nulls. */
  channelNames: (string | null)[];
  /** Channel profile photo URLs aligned to `platforms` by index. May contain nulls. */
  channelProfilePhotoUrls: (string | null)[];
  /** Lifecycle stage of the client team — drives the lifecycle pill in the popover. */
  lifecycleStage: 'active' | 'onboarding' | 'churning' | 'paused';
  /** Raw analytics for the popover's metrics grid (winning version's totals). */
  totalImpressions: number;
  totalReach: number;
  totalEngagements: number;
  metric: string;
  metricValue: number;
  reason: string;
  postedAt: string;
  engagementRate?: number;
  /** Team logo URL — shown instead of the colored initial when present. */
  clientLogoUrl?: string | null;
}

export interface PortfolioRepurposeCandidate extends PortfolioHighlightPost {
  publishedAt: string;
  daysAgo: number;
  originalMetric: string;
  originalMetricValue: number;
}

export interface GetHighlightsResponse {
  topPosts: PortfolioHighlightPost[];
  repurposeCandidates: PortfolioRepurposeCandidate[];
}

// ── Saved portfolio views ─────────────────────────────────────────────────────

export type PortfolioViewType = 'GRID' | 'BOARD' | 'TIMELINE';

export interface PortfolioViewFilters {
  preset?: string;
  ownerId?: number | null;
  visibleBadges?: string[];
  [key: string]: unknown;
}

export interface PortfolioViewRecord {
  id: number;
  name: string;
  filters: PortfolioViewFilters;
  viewType: PortfolioViewType;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePortfolioViewPayload {
  name: string;
  filters?: PortfolioViewFilters;
  viewType?: PortfolioViewType;
  isDefault?: boolean;
}

export interface UpdatePortfolioViewPayload {
  name?: string;
  filters?: PortfolioViewFilters;
  viewType?: PortfolioViewType;
  isDefault?: boolean;
}
